import { useNavigate } from "react-router-dom";
import { Phone, MessageSquare, Mail, MapPin, Clock, Instagram, Facebook, Linkedin, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import { scrollToId } from "./Hero";
import { FOOTER_NAV_ITEMS } from "../../lib/data";
import { useCompany } from "../../context/CompanyContext";

// VOOR BLACK footer — a live view of the centralized Company Contact record.
// Every displayed value is sourced from useCompany().company (fed by the
// public content endpoint), so anything an admin edits in /admin/content →
// Company Contact automatically appears here without a code change.
const MARQUEE = Array.from({ length: 8 }).map((_, i) => (
  <span key={i} className="mx-6 font-display uppercase tracking-[0.3em] text-[#d6d40033] text-sm">
    Time Is Wealth <span className="text-[#d6d40055]">◆</span> Voor Black
  </span>
));

const LEGAL = [
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Terms and Conditions", path: "/terms" },
  { label: "Reservation Policy", path: "/reservation-policy" },
  { label: "Cancellation and Refund Policy", path: "/cancellation-refund-policy" },
  { label: "California Privacy Notice", path: "/california-privacy" },
  { label: "Accessibility Statement", path: "/accessibility" },
  { label: "Account Deletion", path: "/account-deletion" },
];

// WhatsApp glyph (Lucide has no first-party icon).
const WA_ICON = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12.07 0C5.5 0 .17 5.33.17 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.65a11.9 11.9 0 0 0 5.75 1.47h.01c6.57 0 11.9-5.33 11.9-11.9 0-3.18-1.24-6.17-3.46-8.44Zm-8.45 18.32h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.66-.24-.38a9.87 9.87 0 0 1-1.5-5.25c0-5.46 4.44-9.9 9.9-9.9 2.65 0 5.13 1.03 7 2.9a9.85 9.85 0 0 1 2.9 7c-.01 5.47-4.45 9.9-9.91 9.9Zm5.44-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.7.62.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z"/>
  </svg>
);

const Social = ({ icon: Icon, label }) => (
  <button
    type="button"
    onClick={() =>
      toast(`${label[0].toUpperCase() + label.slice(1)} — coming soon`, {
        description: "This page is being prepared.",
      })
    }
    data-testid={`social-${label}`}
    aria-label={`VOOR BLACK on ${label} — coming soon`}
    className="h-10 w-10 rounded-full flex items-center justify-center text-[#c9c9ba] hover:text-[#d6d400] transition-colors"
    style={{ border: "1.5px solid rgba(214,212,0,0.25)" }}
  >
    <Icon size={16} />
  </button>
);

// One-line contact chip. Handles missing values gracefully by returning null.
const ChannelChip = ({ icon: Icon, iconNode, label, value, href, testId }) => {
  if (!value) return null;
  const Cmp = href ? "a" : "span";
  return (
    <Cmp
      {...(href ? { href } : {})}
      data-testid={testId}
      className={`inline-flex items-center gap-2 font-body text-[13px] text-[#c9c9ba] ${href ? "hover:text-[#d6d400] transition-colors" : ""}`}
    >
      <span className="text-[#d6d400] shrink-0 inline-flex items-center">{iconNode || <Icon size={14} />}</span>
      <span className="truncate">
        <span className="hidden sm:inline text-[10px] tracking-[0.24em] uppercase text-[#8f9080] mr-1.5">{label}</span>
        {value}
      </span>
    </Cmp>
  );
};

export default function Footer() {
  const navigate = useNavigate();
  const { company, content, openContactModal } = useCompany();
  const c = company || {};

  // Derived channel values with sensible display fallbacks. Every underlying
  // value comes from company_contact — the fallbacks only apply while the
  // public content endpoint is loading on first render.
  const phoneDisplay = c.phone || "+1 (510) 900-9007";
  const phoneHref = c.phone_e164 || c.phone || "+15109009007";
  const smsDisplay = c.sms_number || c.phone;
  const smsHref = c.sms_e164 || c.phone_e164 || phoneHref;
  const waNumber = (c.whatsapp_number || "").replace(/[^\d+]/g, "");
  const primaryEmail = c.primary_email || "info@voorblack.com";
  const secondaryEmail = c.secondary_email || null;
  const locationDisplay = c.address_line1 || [c.city, c.state].filter(Boolean).join(", ") || "Los Angeles, CA";
  const businessHours = [{ day: "Every Day", hours: "24/7" }];
  // Service areas are managed at the top of website_content — prefer that
  // list; fall back to the company sub-object if a future edit stashes them there.
  const _allAreas = (content?.service_areas && content.service_areas.length > 0)
    ? content.service_areas
    : (c.service_areas || []);
  const serviceAreasList = _allAreas.slice(0, 8);
  const totalServiceAreas = _allAreas.length;

  // Router-aware nav: `path`-tagged items go to their route; anchor-only
  // items scroll on the landing page (or route home first when elsewhere).
  const goNav = (item) => {
    if (item.path) return navigate(item.path);
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(item.id), 60);
    } else {
      scrollToId(item.id);
    }
  };
  const comingSoon = (label) =>
    toast(`${label} — coming soon`, {
      description: `Contact us at ${primaryEmail} for details.`,
    });

  return (
    <footer id="footer" data-testid="footer" className="mt-8 sm:mt-14">
      {/* editorial marquee */}
      <div className="overflow-hidden border-y border-[#d6d40018] py-4">
        <div className="vb-marquee-track">
          {MARQUEE}
          {MARQUEE}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-5 py-8 sm:py-10">
        {/* Row 1 — logo + primary phone + Get In Touch CTA + social */}
        <div className="grid gap-6 lg:grid-cols-[auto,1fr,auto] items-center">
          <button
            onClick={() => (window.location.pathname === "/" ? scrollToId("home") : navigate("/"))}
            className="flex items-center gap-3" data-testid="footer-logo"
          >
            <img
              src="/voor-logo.png"
              alt="VOOR BLACK"
              className="h-11 w-11 object-contain"
              style={{ mixBlendMode: "lighten", filter: "brightness(1.4) contrast(1.05)" }}
            />
            <span className="font-display font-bold tracking-[0.22em] text-[#d6d400]">
              {c.company_name || "VOOR BLACK"}
            </span>
          </button>

          {/* Primary contact row — Call / Text / WhatsApp / Emails */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-center lg:justify-start">
            <ChannelChip icon={Phone} label="Call" value={phoneDisplay}
              href={`tel:${phoneHref}`} testId="footer-phone" />
            <ChannelChip icon={MessageSquare} label="Text" value={smsDisplay}
              href={`sms:${smsHref}`} testId="footer-sms" />
            <ChannelChip iconNode={WA_ICON} label="WhatsApp" value={c.whatsapp_number}
              href={waNumber ? `https://wa.me/${waNumber.replace(/^\+/, "")}` : null}
              testId="footer-whatsapp" />
            <ChannelChip icon={Mail} label="Primary" value={primaryEmail}
              href={`mailto:${primaryEmail}`} testId="footer-email" />
            <ChannelChip icon={Mail} label="Contract and Partnership" value={secondaryEmail}
              href={secondaryEmail ? `mailto:${secondaryEmail}` : null}
              testId="footer-email-secondary" />
            <ChannelChip icon={MapPin} label="Base" value={locationDisplay} testId="footer-location" />
          </div>

          <div className="flex items-center gap-3 justify-center lg:justify-end">
            <button
              onClick={openContactModal}
              data-testid="footer-get-in-touch"
              className="vb-btn inline-flex items-center gap-2 px-5 py-2 text-[11px] uppercase tracking-[0.16em]"
            >
              <Send size={13} /> Get In Touch
            </button>
            <Social icon={Instagram} label="instagram" />
            <Social icon={Facebook} label="facebook" />
            <Social icon={Linkedin} label="linkedin" />
          </div>
        </div>

        <div className="vb-divider mt-7">
          <span className="text-[9px] rotate-45 inline-block">◆</span>
        </div>

        {/* Row 2 — Business Hours + Service Areas (both dynamic from admin) */}
        {(businessHours.length > 0 || serviceAreasList.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            {businessHours.length > 0 && (
              <div data-testid="footer-hours">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={13} className="text-[#d6d400]" />
                  <span className="font-body text-[10px] tracking-[0.28em] uppercase text-[#8f9080]">Business Hours</span>
                </div>
                <ul className="space-y-1">
                  {businessHours.map((h, i) => (
                    <li key={i} className="flex items-center justify-between font-body text-[12.5px]">
                      <span className="text-[#c9c9ba]">{h.day}</span>
                      <span className="text-[#d6d400] font-display tracking-wider">{h.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(
              <div data-testid="footer-areas">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={13} className="text-[#d6d400]" />
                  <span className="font-body text-[10px] tracking-[0.28em] uppercase text-[#8f9080]">Service Area</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/service-areas")}
                  className="text-left font-display font-bold uppercase tracking-[0.14em] text-[#d6d400] leading-snug hover:brightness-125 transition"
                  data-testid="footer-service-area-line"
                  style={{ fontSize: "clamp(11px, 1.05vw, 14px)" }}
                >
                  <span className="block">Serving Los Angeles County and Orange County</span>
                  <span className="block mt-1">To Anywhere Or Back</span>
                </button>
              </div>
            )}
          </div>
        )}

        <div className="vb-divider mt-8">
          <span className="text-[9px] rotate-45 inline-block">◆</span>
        </div>

        {/* Navigation shortcuts — About / Contact / Services / Corporate / etc.
            Anchor items scroll on landing; path items route directly. */}
        <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mt-7">
          {FOOTER_NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              data-testid={`footer-nav-${item.id}`}
              onClick={() => goNav(item)}
              className="font-body uppercase tracking-[0.14em] text-xs text-[#c9c9ba] hover:text-[#d6d400] transition-colors"
            >
              {item.label}
            </button>
          ))}
          <button
            data-testid="footer-nav-account"
            onClick={() => navigate("/account")}
            className="font-body uppercase tracking-[0.14em] text-xs text-[#c9c9ba] hover:text-[#d6d400] transition-colors"
          >
            Account
          </button>
          <a
            href="/discover"
            data-testid="footer-nav-transportation-info"
            className="font-body uppercase tracking-[0.14em] text-xs text-[#c9c9ba] hover:text-[#d6d400] transition-colors"
          >
            Transportation Info
          </a>
        </nav>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-7">
          <button
            onClick={() => navigate("/reserve")}
            className="flex items-center gap-2 font-display uppercase tracking-[0.2em] text-[#d6d400] text-xs hover:brightness-125 transition"
            data-testid="footer-cta"
          >
            Time Is Wealth <ArrowRight size={13} />
          </button>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {LEGAL.map((item) => (
              <button
                key={item.path}
                type="button"
                onClick={() => navigate(item.path)}
                data-testid={`legal-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="font-body text-[11px] text-[#8f9080] hover:text-[#d6d400] transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>

          <p className="font-body text-[#8f9080] text-xs tracking-wide">
            © {new Date().getFullYear()} {c.company_name || "Voor Black"}. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
